/**
 * A agenda do plano: datas em semanas, e treinos por dia.
 *
 * Fica fora dos componentes porque é aritmética de data, e aritmética de data
 * erra em silêncio — um dia a mais no fim da semana, um fuso que empurra tudo
 * para o dia anterior. Aqui isso falha em milissegundos num teste; na tela,
 * falharia num plano publicado.
 *
 * **Tudo em UTC, e o dia é uma string.** `new Date('2026-09-14')` já é UTC, mas
 * `getDate()` lê no fuso local — no Brasil isso devolve 13. Usar só os
 * acessores `getUTC*` e voltar para `YYYY-MM-DD` mantém o dia que a pessoa
 * escolheu igual ao dia que o servidor recebe.
 */

/** Um treino marcado, como a tela o edita. Sem identificador: a gravação troca tudo. */
export interface AgendaSlot {
  template_id: string | null;
}

/** Data ISO (`YYYY-MM-DD`) → os treinos daquele dia, em ordem. */
export type Agenda = Record<string, AgendaSlot[]>;

export interface Day {
  /** `YYYY-MM-DD` */
  date: string;
  /** 0 = domingo, como `getUTCDay`. */
  weekday: number;
}

export interface Week {
  /** 1 para a primeira semana do plano. */
  number: number;
  days: Day[];
}

const DIA = 86_400_000;

function toISO(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function parse(iso: string): Date {
  return new Date(`${iso}T00:00:00Z`);
}

export function addDays(iso: string, days: number): string {
  return toISO(new Date(parse(iso).getTime() + days * DIA));
}

/** Dias inteiros entre duas datas, sempre positivo quando `fim >= inicio`. */
export function daysBetween(inicio: string, fim: string): number {
  return Math.round((parse(fim).getTime() - parse(inicio).getTime()) / DIA);
}

export function endOfWeeks(start: string, weeks: number): string {
  // `- 1` porque a primeira semana **inclui** o dia de início. Sem isso, um
  // plano de uma semana terminaria oito dias depois de começar.
  return addDays(start, weeks * 7 - 1);
}

/**
 * Divide o período em semanas de sete dias, começando no dia de início.
 *
 * A semana começa no dia em que o plano começa, e não na segunda-feira. Quem
 * monta um plano numa quarta pensa "primeira semana" a partir da quarta — e
 * alinhar ao calendário criaria uma primeira semana pela metade, que é a
 * primeira coisa que alguém confunde ao contar séries.
 */
export function buildWeeks(start: string, end: string): Week[] {
  const total = daysBetween(start, end) + 1;
  if (total <= 0) return [];

  const semanas: Week[] = [];
  for (let inicio = 0; inicio < total; inicio += 7) {
    const dias: Day[] = [];
    for (let offset = 0; offset < 7 && inicio + offset < total; offset += 1) {
      const date = addDays(start, inicio + offset);
      dias.push({ date, weekday: parse(date).getUTCDay() });
    }
    semanas.push({ number: Math.floor(inicio / 7) + 1, days: dias });
  }
  return semanas;
}

/** Os treinos vindos do servidor, agrupados por dia e ordenados por `sequence`. */
export function toAgenda(
  workouts: Array<{ scheduled_for: string; sequence: number; template_id: string | null }>,
): Agenda {
  const agenda: Agenda = {};
  for (const treino of [...workouts].sort((a, b) => a.sequence - b.sequence)) {
    (agenda[treino.scheduled_for] ??= []).push({ template_id: treino.template_id });
  }
  return agenda;
}

/**
 * De volta ao formato do servidor. **Dia sem treino não vira linha.**
 *
 * Descanso é a ausência de treino, não um treino vazio — mandar uma linha sem
 * modelo faria a publicação ser recusada por "treino sem modelo", que é
 * verdade e não é o que a pessoa quis dizer.
 */
export function toWorkouts(
  agenda: Agenda,
): Array<{ scheduled_for: string; sequence: number; template_id: string | null }> {
  const linhas = [];
  for (const date of Object.keys(agenda).sort()) {
    const slots = agenda[date] ?? [];
    for (const [sequence, slot] of slots.entries()) {
      linhas.push({ scheduled_for: date, sequence, template_id: slot.template_id });
    }
  }
  return linhas;
}

/**
 * Copia uma semana para a seguinte — o substituto da recorrência.
 *
 * A decisão está registrada no plano do Marco 2 (§5.7): sem recorrência no P0,
 * só datas explícitas. Repetir por cópia dá ao profissional a liberdade de
 * ajustar cada semana depois, que é o comportamento real de quem acompanha
 * alguém — a semana três raramente é igual à um.
 *
 * **Sobrescreve o destino.** Copiar por cima é o gesto esperado de "repetir
 * esta semana"; mesclar deixaria treinos antigos entre os novos, e o resultado
 * seria uma semana que ninguém montou.
 */
export function repeatWeek(agenda: Agenda, week: Week, into: Week): Agenda {
  const copia: Agenda = { ...agenda };

  for (const [indice, dia] of into.days.entries()) {
    const origem = week.days[indice];
    const treinos = origem ? agenda[origem.date] : undefined;
    if (treinos && treinos.length > 0) {
      copia[dia.date] = treinos.map((slot) => ({ ...slot }));
    } else {
      delete copia[dia.date];
    }
  }

  return copia;
}
